import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Clock, Plus, AlertTriangle, CheckCircle2, Search,
  Calendar, Shield, Wrench
} from 'lucide-react';

const SEVERITY_COLORS = {
  blocker: 'bg-red-100 text-red-800', major: 'bg-orange-100 text-orange-800',
  minor: 'bg-yellow-100 text-yellow-800', observation: 'bg-slate-100 text-slate-600',
};

const STATUS_COLORS = {
  open: 'bg-red-100 text-red-700', assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-cyan-100 text-cyan-700', rectified_by_subcontractor: 'bg-teal-100 text-teal-700',
  closed: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
  deferred: 'bg-slate-100 text-slate-600',
};

export default function PostHandover({ projectId, settings }) {
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [packages, setPackages] = useState([]);

  const profile = settings?.profile || {};
  const postTerm = profile.post_completion_term || 'Post-Handover';

  const [form, setForm] = useState({
    description_short: '', location_text: '', severity: 'minor',
    trade_code: '', assigned_company: '', package_id: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [defRes, pkgRes] = await Promise.all([
        api.closeout.listDefects(projectId, { contract_mode: 'post_handover' }),
        api.closeout.listPackages(projectId),
      ]);
      setDefects(defRes.data.items || defRes.data || []);
      setPackages(pkgRes.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!form.description_short.trim()) { toast.error('Description required'); return; }
    try {
      await api.closeout.createDefect(projectId, {
        ...form,
        contract_mode: 'post_handover',
        source_type: 'warranty',
      });
      toast.success(`${postTerm} defect created`);
      setShowCreate(false);
      setForm({ description_short: '', location_text: '', severity: 'minor', trade_code: '', assigned_company: '', package_id: '' });
      loadData();
    } catch { toast.error('Failed to create'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const filtered = defects.filter(d => {
    if (filterStatus && d.status !== filterStatus) return false;
    if (search && !d.description_short?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: defects.length,
    open: defects.filter(d => !['closed', 'waived', 'deferred'].includes(d.status)).length,
    closed: defects.filter(d => d.status === 'closed').length,
    overdue: defects.filter(d => d.target_rectification_date && d.target_rectification_date < new Date().toISOString().split('T')[0] && !['closed', 'waived'].includes(d.status)).length,
  };

  return (
    <div className="space-y-4" data-testid="post-handover">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          {postTerm}
          <span className="ml-2 text-sm font-normal text-slate-400">({stats.total} defects)</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowCreate(true)} data-testid="create-post-handover-btn">
          <Plus className="h-4 w-4" /> Log {postTerm} Defect
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-[10px] text-slate-500 uppercase">Total</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-red-600">{stats.open}</p><p className="text-[10px] text-slate-500 uppercase">Open</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{stats.closed}</p><p className="text-[10px] text-slate-500 uppercase">Closed</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-600">{stats.overdue}</p><p className="text-[10px] text-slate-500 uppercase">Overdue</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Search defects..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" />
        </div>
        <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Defect List */}
      {filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-lg font-medium text-slate-500">No {postTerm.toLowerCase()} defects</p>
          <p className="text-sm text-slate-400 mt-1">Defects reported during the {postTerm.toLowerCase()} period will appear here</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => (
            <Card key={d.id} className="hover:shadow-sm transition-shadow" data-testid={`post-defect-${d.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-slate-400">{d.defect_no}</span>
                      <Badge className={`text-[10px] ${SEVERITY_COLORS[d.severity]}`}>{d.severity}</Badge>
                      <Badge className={`text-[10px] ${STATUS_COLORS[d.status] || 'bg-slate-100'}`}>{d.status?.replace(/_/g, ' ')}</Badge>
                    </div>
                    <p className="text-sm font-medium text-slate-800">{d.description_short}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      {d.location_text && <span>{d.location_text}</span>}
                      {d.trade_code && <span>{d.trade_code.replace(/_/g, ' ')}</span>}
                      {d.assigned_company && <span>Assigned: {d.assigned_company}</span>}
                      {d.target_rectification_date && (
                        <span className={`flex items-center gap-1 ${d.target_rectification_date < new Date().toISOString().split('T')[0] && !['closed', 'waived'].includes(d.status) ? 'text-red-500 font-medium' : ''}`}>
                          <Calendar className="h-3 w-3" />Due: {d.target_rectification_date}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Log {postTerm} Defect</DialogTitle>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Description *</Label><Input value={form.description_short} onChange={e => setForm(f => ({...f, description_short: e.target.value}))} placeholder="Describe the defect" data-testid="post-defect-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Location</Label><Input value={form.location_text} onChange={e => setForm(f => ({...f, location_text: e.target.value}))} placeholder="e.g. Level 3, Unit 5A" /></div>
              <div><Label className="text-xs">Severity</Label>
                <Select value={form.severity} onValueChange={v => setForm(f => ({...f, severity: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="blocker">Blocker</SelectItem>
                    <SelectItem value="observation">Observation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Assigned Company</Label><Input value={form.assigned_company} onChange={e => setForm(f => ({...f, assigned_company: e.target.value}))} placeholder="Subcontractor company" /></div>
            {packages.length > 0 && (
              <div><Label className="text-xs">Package (optional)</Label>
                <select className="w-full h-9 border rounded-md px-3 text-sm bg-white" value={form.package_id} onChange={e => setForm(f => ({...f, package_id: e.target.value}))}>
                  <option value="">No package</option>
                  {packages.map(p => <option key={p.id} value={p.id}>{p.package_name}</option>)}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleCreate} data-testid="create-post-defect-submit"><Plus className="h-4 w-4 mr-1" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
